import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ToastProvider } from "@/lib/toast";
import { AppShell } from "@/components/layout/AppShell";
import { LoadingState } from "@/components/common/States";
import LoginPage from "@/pages/Login";
import AcessoNegadoPage from "@/pages/AcessoNegado";
// Fila de hoje e a rota padrao (home) — carregada de imediato para o primeiro
// paint. As demais telas sao code-split: Kanban em particular carrega
// @dnd-kit, que nao vale pagar no bundle inicial de quem so abre a fila.
import FilaHojePage from "@/pages/FilaHoje";
const KanbanPage = lazy(() => import("@/pages/Kanban"));
const LeadsPage = lazy(() => import("@/pages/Leads"));
const DashboardPage = lazy(() => import("@/pages/Dashboard"));
const ScriptsPage = lazy(() => import("@/pages/Scripts"));
const ClientesFechadosPage = lazy(() => import("@/pages/ClientesFechados"));
const FinanceiroPage = lazy(() => import("@/pages/Financeiro"));
const ProdutosPage = lazy(() => import("@/pages/Produtos"));
const HomePage = lazy(() => import("@/pages/Home"));
const UsuariosPage = lazy(() => import("@/pages/Usuarios"));
const TarefasPage = lazy(() => import("@/pages/organizacao/Tarefas"));
const NotasPage = lazy(() => import("@/pages/organizacao/Notas"));
const ReunioesPage = lazy(() => import("@/pages/organizacao/Reunioes"));
const RotinasPage = lazy(() => import("@/pages/organizacao/Rotinas"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: 1,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <AuthGate />
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}

function AuthGate() {
  const { status } = useAuth();

  if (status === "loading" || status === "checking_allowlist") {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <LoadingState label="Verificando sessão…" />
      </div>
    );
  }

  if (status === "signed_out") {
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  if (status === "not_allowed") {
    return (
      <Routes>
        <Route path="*" element={<AcessoNegadoPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route
          path="/"
          element={
            <Suspense fallback={<LoadingState label="Carregando…" />}>
              <HomePage />
            </Suspense>
          }
        />
        <Route path="/clientes" element={<FilaHojePage />} />
        <Route
          path="/clientes/kanban"
          element={
            <Suspense fallback={<LoadingState label="Carregando kanban…" />}>
              <KanbanPage />
            </Suspense>
          }
        />
        <Route
          path="/clientes/leads"
          element={
            <Suspense fallback={<LoadingState label="Carregando leads…" />}>
              <LeadsPage />
            </Suspense>
          }
        />
        <Route
          path="/clientes/fechados"
          element={
            <Suspense fallback={<LoadingState label="Carregando clientes…" />}>
              <ClientesFechadosPage />
            </Suspense>
          }
        />
        <Route
          path="/financeiro"
          element={
            <Suspense fallback={<LoadingState label="Carregando financeiro…" />}>
              <FinanceiroPage />
            </Suspense>
          }
        />
        <Route
          path="/organizacao/tarefas"
          element={
            <Suspense fallback={<LoadingState label="Carregando tarefas…" />}>
              <TarefasPage />
            </Suspense>
          }
        />
        <Route
          path="/organizacao/reunioes"
          element={
            <Suspense fallback={<LoadingState label="Carregando reuniões…" />}>
              <ReunioesPage />
            </Suspense>
          }
        />
        <Route
          path="/organizacao/rotinas"
          element={
            <Suspense fallback={<LoadingState label="Carregando rotinas…" />}>
              <RotinasPage />
            </Suspense>
          }
        />
        <Route
          path="/organizacao/notas"
          element={
            <Suspense fallback={<LoadingState label="Carregando notas…" />}>
              <NotasPage />
            </Suspense>
          }
        />
        <Route
          path="/produtos"
          element={
            <Suspense fallback={<LoadingState label="Carregando produtos…" />}>
              <ProdutosPage />
            </Suspense>
          }
        />
        <Route
          path="/admin/usuarios"
          element={
            <Suspense fallback={<LoadingState label="Carregando usuários…" />}>
              <UsuariosPage />
            </Suspense>
          }
        />
        <Route
          path="/clientes/dashboard"
          element={
            <Suspense fallback={<LoadingState label="Carregando dashboard…" />}>
              <DashboardPage />
            </Suspense>
          }
        />
        <Route
          path="/clientes/scripts"
          element={
            <Suspense fallback={<LoadingState label="Carregando scripts…" />}>
              <ScriptsPage />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
